import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ChevronRight, X } from "lucide-react";
import { QUIZ, scoreQuiz } from "@/data/content";
import { useQuiz } from "@/context/QuizContext";
import { LeadForm } from "./LeadForm";
import { getLenis } from "@/hooks/useLenis";

export const QuizModal = () => {
  const { open, closeQuiz, source, fitSignals } = useQuiz();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const score = scoreQuiz(answers, fitSignals);

  useEffect(() => {
    if (open) { setStep(0); setAnswers([]); getLenis()?.stop(); document.body.style.overflow = "hidden"; }
    else { getLenis()?.start(); document.body.style.overflow = ""; }
    return () => { getLenis()?.start(); document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeQuiz();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeQuiz]);

  const done = step >= QUIZ.length;
  const progress = Math.round((Math.min(step, QUIZ.length) / QUIZ.length) * 100);

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="quiz-modal-overlay" className="fixed inset-0 z-[100] grid place-items-center p-4 bg-[rgba(2,12,14,.8)] backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeQuiz}>
          <motion.div
            data-testid="quiz-modal" data-lenis-prevent role="dialog" aria-modal="true" aria-labelledby="quiz-title"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[560px] max-h-[calc(100vh-32px)] overflow-y-auto rounded-3xl bg-[#f7f3eb] text-[var(--ink)] p-7 sm:p-9 shadow-[0_40px_120px_rgba(0,0,0,.5)]"
          >
            <div className="flex items-center justify-between font-mono text-[11px] tracking-[.14em] uppercase text-[var(--muted)]">
              <span data-testid="quiz-step-label">{done ? "Your next step" : `Question ${step + 1} of ${QUIZ.length}`}</span>
              <button data-testid="quiz-close-button" aria-label="Close" onClick={closeQuiz} className="grid place-items-center w-9 h-9 rounded-full hover:bg-black/5"><X size={18} /></button>
            </div>
            <div className="h-[3px] my-4 bg-[#ddd5c8] overflow-hidden rounded-full"><motion.span className="block h-full bg-[var(--gold)]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} /></div>

            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                  <p className="eyebrow">{QUIZ[step].kicker}</p>
                  <h3 id="quiz-title" className="font-serif text-3xl sm:text-4xl leading-[1.05] m-0 mb-6">{QUIZ[step].title}</h3>
                  <div className="grid gap-3">
                    {QUIZ[step].options.map((opt, i) => (
                      <button key={opt} data-testid={`quiz-option-${step}-${i}`} onClick={() => { setAnswers((a) => { const n = [...a]; n[step] = opt; return n; }); setStep((s) => s + 1); }} className="flex items-center justify-between min-h-[62px] px-5 rounded-xl border border-[#d5ccbd] bg-white/60 text-left font-medium hover:border-[var(--gold)] hover:bg-white transition-colors">
                        <span>{opt}</span><ChevronRight size={18} className="text-[#9a7225]" />
                      </button>
                    ))}
                  </div>
                  {step > 0 && <button data-testid="quiz-back-button" onClick={() => setStep((s) => s - 1)} className="mt-5 inline-flex items-center gap-2 text-sm text-[#87651f] font-semibold"><ArrowLeft size={15} /> Back</button>}
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                  <p className="eyebrow">A good next step</p>
                  <h3 id="quiz-title" className="font-serif text-3xl sm:text-[38px] leading-[1.05] m-0 mb-3">Start with a conversation, not an application.</h3>
                  <p className="text-sm text-[var(--muted)] leading-relaxed m-0 mb-5">Leave your details and Jian Da's team will reach out for a relaxed, private chat about your background and whether Affinity feels right for you.</p>
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span data-testid="quiz-fit-score" className="px-3 py-1.5 rounded-full text-[12px] font-bold bg-[var(--gold)] text-[var(--navy)]">Fit {score}%</span>
                    {answers.map((a) => <span key={a} data-testid="quiz-summary-chip" className="px-3 py-1.5 rounded-full text-[12px] border border-[#d8cdbc] bg-white/60 text-[#5f6b6d]">✓ {a}</span>)}
                  </div>
                  <LeadForm compact quizAnswers={answers} fitScore={score} source={`quiz:${source}`} />
                  <button data-testid="quiz-restart-button" onClick={() => { setStep(0); setAnswers([]); }} className="mt-5 text-sm text-[#87651f] font-semibold">Start again</button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
